import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_PREFERENCES, type UserPreferences } from '@monrdv/shared';
import { request } from './api.js';

/**
 * User preferences.
 *
 * Kept server-side so a coordinator's choices follow them to another machine, and read
 * through the query cache so every component sees the same value without prop-drilling.
 *
 * Falls back to the defaults rather than blocking: a preference that has not loaded yet must
 * never stop a screen rendering, and `peek` is a safe default because it is the least
 * destructive of the three (it does not navigate away from unsaved work).
 */
export function usePreferences() {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['preferences'],
    queryFn: () => request<UserPreferences>('/me/preferences'),
    staleTime: 10 * 60_000,
    retry: false,
  });

  const update = useMutation({
    mutationFn: (patch: Partial<UserPreferences>) =>
      request<UserPreferences>('/me/preferences', { method: 'PATCH', body: JSON.stringify(patch) }),
    // Applied immediately: changing "how records open" and then having to wait for a round
    // trip before the next click behaves differently feels broken.
    onMutate: async (patch) => {
      const previous = queryClient.getQueryData<UserPreferences>(['preferences']);
      queryClient.setQueryData<UserPreferences>(['preferences'], {
        ...(previous ?? DEFAULT_PREFERENCES),
        ...patch,
      });
      return { previous };
    },
    onError: (_error, _patch, context) => {
      if (context?.previous) queryClient.setQueryData(['preferences'], context.previous);
    },
    onSuccess: (fresh) => queryClient.setQueryData(['preferences'], fresh),
  });

  return {
    preferences: query.data ?? DEFAULT_PREFERENCES,
    isLoaded: query.isSuccess,
    update: update.mutate,
    isSaving: update.isPending,
  };
}
